import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import {
  createSiUnit,
  getSiUnits,
  updateSiUnit,
} from "../../actions/siUnitActions";
import { RESET_SI_UNIT } from "constants/siUnitConstants";
import { dashboardApi } from "../../api/index.js";
import AppSelect from "../../components/ui/AppSelect";

/**
 * Add / Edit an SI unit.
 * ─────────────────────────────────────────────────────────────────────────
 * DUPLICATE GUARD. This list is a dropdown on every product form, so a
 * second "Boxes" is not clutter — it silently splits one real unit across
 * two ids that nobody can merge afterwards, because live products already
 * reference both.
 *
 * The guard exists in two places on purpose, and they are not redundant:
 *
 *   HERE — so the admin is told BEFORE they submit, while the field is
 *   still in front of them. Purely advisory; it can be stale, and it is
 *   trivially bypassed by anything that is not this form.
 *
 *   THE SERVER — a case-insensitive check plus a collated unique index,
 *   which is the only guarantee that survives two admins typing at once.
 *   Its 409 is surfaced verbatim below the field.
 *
 * Comparison is exact-but-case-insensitive, with whitespace normalised, to
 * match the server. Deliberately NOT fuzzy: Box/Boxes, Inch/Inches and
 * Pc/Pcs are all live rows with products on them, and a plural-aware rule
 * would refuse rows that exist.
 *
 * DUPLICATES THAT ALREADY EXIST are handled with "New products use": a row
 * such as "Kgs" points at "Kg", keeps its id and its products, and stops being
 * offered on the product forms. Family groups the forms' unit picker.
 */
const normaliseUnit = (value) =>
  typeof value === "string" ? value.trim().replace(/\s+/g, " ") : "";

const SiUnitForm = () => {
  const [postData, setPostData] = useState({
    unit: "",
    family: "",
    preferredUnitId: "",
  });
  const [families, setFamilies] = useState([]);

  useEffect(() => {
    let cancelled = false;
    dashboardApi
      .get("/category/groups")
      .then(({ data }) => {
        if (!cancelled && Array.isArray(data?.unitFamilies)) setFamilies(data.unitFamilies);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const [saving, setSaving] = useState(false);
  /* The server's own words for a rejection — a 409 message, or whatever
     else went wrong. Cleared as soon as the field is edited, so a stale
     complaint never sits under a value it no longer describes. */
  const [serverError, setServerError] = useState("");

  const clear = () => {
    setPostData({ unit: "", family: "", preferredUnitId: "" });
    setServerError("");
  };

  const siUnitsState = useSelector((state) => state.siUnits);
  const { siUnit, siUnits: unitList, loading } = siUnitsState || {};

  const dispatch = useDispatch();
  const history = useHistory();

  const list = useMemo(
    () => (Array.isArray(unitList) ? unitList : []),
    [unitList]
  );

  /* The form is reachable directly by URL, so the list it checks against
     cannot be assumed to be in the store already — and even when it is, it
     may be stale, which for a duplicate check is the same as absent.
     Fetch once per mount.

     The ref, rather than an empty dep array with a `list.length` test, is
     deliberate: a `react-hooks/exhaustive-deps` disable comment is a HARD
     BUILD FAILURE in this app (top-level eslint@7 vs react-scripts 5, so
     the rule is undefined at build time and naming it is an error). This
     effect closes over nothing but `dispatch` and the ref, so the deps are
     honestly exhaustive and no directive is needed. */
  const requestedList = useRef(false);
  useEffect(() => {
    if (requestedList.current) return;
    requestedList.current = true;
    dispatch(getSiUnits());
  }, [dispatch]);

  const trimmed = normaliseUnit(postData.unit);

  /* The existing row this value would collide with, if any. Editing a unit
     never collides with itself, which is what lets "kgs" be corrected to
     "Kgs" without the guard blocking its own record. */
  const conflict = useMemo(() => {
    if (!trimmed) return null;
    const key = trimmed.toLowerCase();
    return (
      list.find(
        (row) =>
          row?._id !== siUnit?._id &&
          normaliseUnit(row?.unit).toLowerCase() === key
      ) || null
    );
  }, [list, trimmed, siUnit]);

  const blocked = !trimmed || !!conflict || saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (blocked) return;

    setSaving(true);
    setServerError("");

    const payload = {
      ...postData,
      unit: trimmed,
      family: postData.family || null,
      preferredUnitId: postData.preferredUnitId || null,
    };
    const editing = !!siUnit?._id;
    const result = editing
      ? await dispatch(updateSiUnit(siUnit._id, payload))
      : await dispatch(createSiUnit(payload));

    setSaving(false);

    /* Only announce success once the server has actually said so. */
    if (!result?.ok) {
      const message =
        result?.message ||
        (editing ? "Failed to update the unit." : "Failed to add the unit.");
      setServerError(message);
      toast.error(message);
      /* A 409 means the store's copy of the list is behind the database —
         refresh it so the inline guard stops disagreeing with the server. */
      if (result?.duplicate) dispatch(getSiUnits());
      return;
    }

    if (editing) dispatch({ type: RESET_SI_UNIT });
    toast.success(editing ? "Unit Updated!" : "Unit Added!");
    clear();
    history.push("/admin/units");
  };

  useEffect(() => {
    if (siUnit?._id) {
      setPostData({
        ...siUnit,
        family: siUnit.family || "",
        preferredUnitId: siUnit.preferredUnitId ? String(siUnit.preferredUnitId) : "",
      });
      setServerError("");
    } else {
      clear();
    }
  }, [siUnit]);

  const feedback = conflict
    ? `"${conflict.unit}" is already in the list.`
    : serverError;

  return (
    <>
      {loading ? (
        <Spinner
          animation="grow"
          variant="danger"
          style={{
            marginTop: "70px",
            top: "50%",
            left: "50%",
            position: "fixed",
          }}
        />
      ) : (
        <Col>
          <Form id="deliveryZone" autoComplete="off" onSubmit={handleSubmit}>
            <Card>
              <Card.Title className="pt-2 p-2" as="h4">
                Add || Edit SI Unit
              </Card.Title>
              <Card.Body className="pt-0">
                <Col md={6}>
                  <Form.Group>
                    <Card.Body>
                      <label htmlFor="si-unit-name">Unit</label>
                      <Form.Control
                        id="si-unit-name"
                        name="unit"
                        type="text"
                        placeholder="Add Unit..."
                        value={postData.unit}
                        isInvalid={!!feedback}
                        aria-describedby="si-unit-feedback"
                        onChange={(e) => {
                          setServerError("");
                          setPostData({ ...postData, unit: e.target.value });
                        }}
                      ></Form.Control>
                      {feedback ? (
                        <div
                          id="si-unit-feedback"
                          style={{
                            marginTop: 6,
                            fontSize: "0.82rem",
                            color: "#b91c1c",
                            fontWeight: 500,
                          }}
                        >
                          {feedback}
                        </div>
                      ) : (
                        <div
                          id="si-unit-feedback"
                          style={{
                            marginTop: 6,
                            fontSize: "0.78rem",
                            color: "#64748b",
                          }}
                        >
                          Units are matched without case, so "Boxes" and
                          "boxes" count as the same entry.
                        </div>
                      )}
                    </Card.Body>
                    <Card.Body className="pt-0">
                      <label htmlFor="si-unit-family">Family</label>
                      <AppSelect
                        block
                        id="si-unit-family"
                        value={postData.family || ""}
                        onChange={(e) => setPostData({ ...postData, family: e.target.value })}
                      >
                        <option value="">No family</option>
                        {families.map((f) => (
                          <option key={f.key} value={f.key}>
                            {f.label}
                          </option>
                        ))}
                      </AppSelect>
                      <div style={{ marginTop: 6, fontSize: "0.78rem", color: "#64748b" }}>
                        Product forms group units by family, so a vendor picking a
                        weight sees the weights together.
                      </div>
                    </Card.Body>
                    <Card.Body className="pt-0">
                      <label htmlFor="si-unit-preferred">New products use</label>
                      <AppSelect
                        block
                        id="si-unit-preferred"
                        value={postData.preferredUnitId || ""}
                        onChange={(e) => setPostData({ ...postData, preferredUnitId: e.target.value })}
                      >
                        <option value="">This unit</option>
                        {list
                          .filter((row) => row?._id !== siUnit?._id && !row?.preferredUnitId)
                          .sort((a, b) => String(a.unit).localeCompare(String(b.unit)))
                          .map((row) => (
                            <option key={row._id} value={row._id}>
                              {row.unit}
                            </option>
                          ))}
                      </AppSelect>
                      <div style={{ marginTop: 6, fontSize: "0.78rem", color: "#64748b" }}>
                        Pick another unit to mark this one as a duplicate of it. Products
                        that already use this unit keep it; new products are offered the
                        other one.
                      </div>
                    </Card.Body>
                  </Form.Group>
                </Col>
              </Card.Body>

              <Card.Body>
                <Card.Footer className="text-right">
                  <Button
                    className="btn-fill pull-right"
                    variant="danger"
                    onClick={clear}
                    disabled={saving}
                    style={{ marginRight: "5px" }}
                  >
                    Clear
                  </Button>
                  <Button
                    className="btn-fill pull-right"
                    variant="info"
                    type="submit"
                    disabled={blocked}
                  >
                    {saving ? "Saving…" : "Submit"}
                  </Button>
                  <div className="clearfix"></div>
                </Card.Footer>
              </Card.Body>
            </Card>
          </Form>
        </Col>
      )}
    </>
  );
};

export default SiUnitForm;
